import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  Checkbox,
  Input,
  DataTable,
  Column,
  DatePicker,
  SearchablePopover,
  StatusPill,
} from "@racoon/ui";
import { useT, useTypedT, Namespace, TranslationKey } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import {
  JOB_STATUS_TONE,
  STATUS_TONE_CLASS,
  getEnumName,
  matchesAnySearch,
  matchesSearch,
} from "@racoon/lib";
import { IcCancel, IcFilter } from "@racoon/icons";
import { useListJobsQuery } from "~/shared/api/jobs/job.grpc";
import { useListPickupLocationGroupsQuery } from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { JobPickerSelectionStore, useJobPickerSelection } from "./job-picker-selection";
import {
  getJobVolume,
  isPickable,
  jobsForDay,
  roundVolume,
  useIncidentByJob,
} from "./job-picker-jobs";
import { plannedContainerCount } from "~/shared/lib/job-containers";

export interface JobPickerModalContentProps {
  /** Owner/service-provider organizations to scope the query. */
  serviceProviderIds?: bigint[];
  /** Optional truck filter (backend narrows compatible jobs). */
  truckId?: bigint;
  /** Only jobs this organization executes — a route takes its owner's jobs only. */
  executorId?: bigint;
  /** Lists the jobs these organizations execute (executed_only) instead of serviceProviderIds' visible ones. */
  executedBy?: bigint[];
  /** Day to preselect in the date filter. */
  initialDate?: Date;
  /** Job ids already added elsewhere — hidden from the list. */
  excludeJobIds?: number[];
  /** Allow selecting more than one job (default true). */
  multiple?: boolean;
  /** Selection shared with the modal footer. */
  selection: JobPickerSelectionStore;
}

type EnumKey = TranslationKey<Namespace.Enum>;

// The platform's job-status tones. This picker's design (Figma 16119-30653) puts body
// text on «Неназначено» where the shared pending tone is white; overridden here only,
// so the other screens' pills stay as they are until the shared tone is revisited.
const jobStatusTone = (status: ConnectProto.JobStatus) => {
  const name = getEnumName(ConnectProto.JobStatusSchema, status);
  const tone = name ? STATUS_TONE_CLASS[JOB_STATUS_TONE[name]] : undefined;
  if (!tone) return undefined;
  return status === ConnectProto.JobStatus.CREATED
    ? `${tone.bg} text-text-body`
    : `${tone.bg} ${tone.color}`;
};

const INCIDENT_BADGE_CLASS =
  "rounded px-2 py-1 text-xs font-medium tracking-[-0.01px] whitespace-nowrap";

// DataTable left-aligns every cell; the design centres the short ones.
const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="flex w-full justify-center">{children}</span>
);

export const JobPickerModalContent: React.FC<JobPickerModalContentProps> = ({
  serviceProviderIds,
  truckId,
  executorId,
  executedBy,
  initialDate,
  excludeJobIds = [],
  multiple = true,
  selection,
}) => {
  const t = useTypedT(Namespace.Routes);
  const tAny = useT();

  const [search, setSearch] = useState("");
  const selected = useJobPickerSelection(selection);

  // Filters — preselected from props when opened (e.g. from assign-route).
  const [date, setDate] = useState<Date | undefined>(initialDate ?? new Date());
  // Route (pickup location) groups — several may be picked at once.
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  // Opened from a context that already knows the truck, so respecting its
  // capabilities is the default; unchecking widens the list to every job.
  const [respectTruck, setRespectTruck] = useState(truckId != null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const hasCompany = !!serviceProviderIds && serviceProviderIds.length > 0;

  const truckFilter = respectTruck ? truckId : undefined;

  const { data: groupsData } = useListPickupLocationGroupsQuery(
    { ownerOrganizationIds: serviceProviderIds ?? [] },
    { skip: !hasCompany },
  );
  const groups = useMemo(() => groupsData?.pickupLocationGroups ?? [], [groupsData]);

  // One unfiltered request instead of a per-bucket DateTimeFilter each: the day
  // and overdue sets are subsets of it, and jobs with a NULL window match no
  // time filter at all, so changing the date costs no request. Missed jobs
  // (NOT_COMPLETED) are offered too, to be put back on a route.
  //
  // One job per order: a missed order is re-issued as a new job under the same
  // order number, and the order's current job decides whether it is listed —
  // an order whose newest attempt is already routed stays out.
  //
  // The group filter has to go to the backend — a Job carries no group id, so
  // it cannot be resolved client-side from the list alone.
  const { data, isLoading } = useListJobsQuery(
    {
      serviceProviderIds: executedBy ?? serviceProviderIds ?? [],
      executedOnly: !!executedBy,
      jobStatuses: [ConnectProto.JobStatus.CREATED, ConnectProto.JobStatus.NOT_COMPLETED],
      truckId: truckFilter,
      pickupLocationGroupIds: groupFilter.map((id) => BigInt(id)),
      onePerOrder: true,
    },
    { skip: !hasCompany },
  );

  const excludeSet = useMemo(() => new Set(excludeJobIds), [excludeJobIds]);

  const allJobs = useMemo(() => data?.jobs ?? [], [data]);

  // Taken from the whole list, not the searched one — typing must not refetch.
  const { incidentByJob, incidentsReady } = useIncidentByJob(
    hasCompany ? serviceProviderIds?.[0] : undefined,
    allJobs,
  );

  const jobs = useMemo(() => {
    const all = jobsForDay(
      allJobs.filter(
        (job) =>
          !excludeSet.has(Number(job.id)) &&
          (!executorId || String(job.serviceProviderId) === String(executorId)),
      ),
      date,
    );
    if (!search.trim()) return all;
    return all.filter((job) =>
      matchesAnySearch(
        [
          job.orderNumber,
          job.pickupLocation?.name,
          job.pickupLocation?.address,
          job.serviceReceiver?.name || job.containerGroup?.ownerOrganization?.name,
          job.id.toString(),
        ],
        search,
      ),
    );
  }, [allJobs, date, excludeSet, executorId, search]);

  const totals = useMemo(() => {
    let containers = 0;
    let volume = 0;
    jobs.forEach((job) => {
      containers += plannedContainerCount(job);
      volume += getJobVolume(job);
    });
    return { count: jobs.length, containers, volume: roundVolume(volume) };
  }, [jobs]);

  const getJobTypeLabel = (job: ConnectProto.Job) =>
    job.type !== undefined
      ? tAny(Namespace.Orders, `types.${ConnectProto.JobType[job.type]}` as any)
      : "—";

  const pickableJobs = useMemo(() => jobs.filter(isPickable), [jobs]);

  const allVisibleSelected =
    pickableJobs.length > 0 && pickableJobs.every((j) => selected.has(j.id.toString()));

  // The job object travels with the id: the consumer's own query may not cover
  // it (the filters here are the picker's own), so it would otherwise
  // have no way to resolve the address, containers or capabilities of a pick.
  const toggleJob = (job: ConnectProto.Job) => {
    if (isPickable(job)) selection.toggle(job.id.toString(), job);
  };

  const toggleAll = () =>
    selection.toggleMany(
      pickableJobs.map((job) => job.id.toString()),
      !allVisibleSelected,
      pickableJobs,
    );

  const groupOptions = useMemo(
    () => groups.map((group) => ({ value: group.id.toString(), label: group.name })),
    [groups],
  );

  const toggleGroup = (id: string) =>
    setGroupFilter((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );

  const columns = useMemo<Column<ConnectProto.Job>[]>(
    () => [
      {
        id: "select",
        header: () =>
          multiple ? (
            <Checkbox
              checked={allVisibleSelected}
              onCheckedChange={toggleAll}
              aria-label="select-all"
            />
          ) : null,
        accessor: () => null,
        width: 64,
        sortable: false,
        cell: ({ row }) => {
          const id = row.original.id.toString();
          return (
            <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selected.has(id)}
                disabled={!isPickable(row.original)}
                onCheckedChange={() => toggleJob(row.original)}
              />
            </div>
          );
        },
      },
      {
        id: "orderNumber",
        header: t("jobPicker.orderNumber"),
        accessor: (row) => row.orderNumber,
        sortable: true,
        width: 130,
        cell: ({ row }) => <Centered>{row.original.orderNumber || "—"}</Centered>,
      },
      {
        id: "status",
        header: t("jobPicker.status"),
        accessor: (row) => row.status,
        sortable: true,
        width: 134,
        cell: ({ row }) => (
          <StatusPill tone={jobStatusTone(row.original.status)} className="w-[118px]">
            {tAny(
              Namespace.Enum,
              getEnumName(ConnectProto.JobStatusSchema, row.original.status) as EnumKey,
            )}
          </StatusPill>
        ),
      },
      {
        id: "incident",
        header: t("jobPicker.incident"),
        accessor: () => null,
        sortable: false,
        width: 136,
        cell: ({ row }) => {
          if (row.original.status !== ConnectProto.JobStatus.NOT_COMPLETED || !incidentsReady) {
            return null;
          }
          const incident = incidentByJob.get(row.original.id);
          if (!incident) return null;
          // EXPIRED («Не успели (долг)») is the system's midnight expiry: the
          // design marks it red, a reported problem orange.
          const tone =
            incident.type === ConnectProto.DriverIncidentType.EXPIRED
              ? "bg-surface-error-light text-text-error"
              : "bg-surface-warning-light text-text-warning";
          return (
            <Centered>
              <span className={`${INCIDENT_BADGE_CLASS} ${tone}`}>
                {tAny(
                  Namespace.Enum,
                  getEnumName(ConnectProto.DriverIncidentTypeSchema, incident.type) as EnumKey,
                )}
              </span>
            </Centered>
          );
        },
      },
      {
        id: "id",
        header: t("jobPicker.jobId"),
        accessor: (row) => `Z${row.id.toString()}`,
        sortable: true,
        width: 109,
        cell: ({ getValue }) => <Centered>{getValue()}</Centered>,
      },
      {
        id: "site",
        header: t("object"),
        accessor: (row) =>
          row.pickupLocation?.name || row.pickupLocation?.address || t("noAddress"),
        sortable: true,
        cell: ({ getValue }) => <span className="truncate px-2">{getValue()}</span>,
      },
      {
        id: "type",
        header: t("jobPicker.requestType"),
        accessor: (row) => getJobTypeLabel(row),
        sortable: true,
        width: 125,
        cell: ({ getValue }) => <span className="px-2">{getValue()}</span>,
      },
      {
        id: "category",
        header: t("jobPicker.workType"),
        accessor: (row) =>
          tAny(
            Namespace.Enum,
            getEnumName(ConnectProto.JobCategorySchema, row.jobCategory) as EnumKey,
          ),
        sortable: true,
        width: 145,
        cell: ({ getValue }) => <span className="px-2">{getValue()}</span>,
      },
      {
        id: "containers",
        header: t("colContainerGroup"),
        accessor: (row) => row.containerGroup?.containerType?.name ?? "",
        sortable: true,
        width: 186,
        cell: ({ row }) => {
          const group = row.original.containerGroup;
          if (!group) return <Centered>—</Centered>;
          return (
            <Centered>
              <span className="flex gap-2">
                {group.containerType?.name || t("container")}
                <span className="text-text-subtle">({plannedContainerCount(row.original)})</span>
              </span>
            </Centered>
          );
        },
      },
      {
        id: "volume",
        header: t("colVolume"),
        accessor: (row) => roundVolume(getJobVolume(row)),
        sortable: true,
        width: 91,
        cell: ({ getValue }) => <Centered>{getValue()}</Centered>,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tAny, multiple, selected, allVisibleSelected, jobs, incidentByJob, incidentsReady],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-4 p-4 pb-0">
        <div className="flex flex-wrap items-center gap-4">
          <DatePicker
            value={date}
            onChange={setDate}
            className="flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-text-body"
          />

          <div className="relative w-full sm:w-[165px]">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-text-subtle" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tAny(Namespace.Common, "search")}
              className="!h-8 rounded-lg border-slate-200 pl-8 text-sm placeholder:text-text-subtle"
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {/* Filters button + panel */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((o) => !o)}
                  className="flex h-8 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-text-body hover:bg-slate-50"
                >
                  <IcFilter className="h-4 w-4 text-text-subtle" />
                  {t("filters")}
                </button>

                {filtersOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                    <div className="absolute right-0 top-full z-50 mt-2 flex w-[260px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
                      <FilterMultiSelect
                        inputId="job-picker-filter-route-group"
                        label={t("routeGroupFilter")}
                        placeholder={tAny(Namespace.Common, "select")}
                        values={groupFilter}
                        options={groupOptions}
                        searchPlaceholder={tAny(Namespace.Common, "search")}
                        emptyText={tAny(Namespace.Common, "noResults")}
                        summary={(count) => t("selectedCountShort", { count })}
                        onToggle={toggleGroup}
                      />
                    </div>
                  </>
                )}
              </div>
              {/* Active filter chips */}
              {groupFilter.map((id) => (
                <FilterChip
                  key={id}
                  label={groupOptions.find((o) => o.value === id)?.label ?? id}
                  onRemove={() => toggleGroup(id)}
                />
              ))}
            </div>
            {/* Respect the capabilities of the truck the caller came in with */}
            {truckId != null && (
              <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium tracking-[-0.2px] text-text-body">
                <Checkbox
                  checked={respectTruck}
                  onCheckedChange={(checked) => setRespectTruck(checked === true)}
                />
                {t("respectTruckCapabilities")}
              </label>
            )}
          </div>
        </div>

        {/* Counts bar */}
        <div className="flex min-h-12 flex-wrap items-center justify-between rounded-lg border border-slate-200 bg-slate-100 px-2">
          <span className="p-2 text-sm font-medium leading-[14px] tracking-[-0.14px] text-text-body">
            {t("jobPicker.title")}
          </span>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-1 px-2 text-xs font-bold uppercase leading-4 text-text-subtle">
            <span>
              {totals.count} {t("applications")}
            </span>
            <span>
              {totals.containers} {t("containersGenitive")}
            </span>
            <span>
              {totals.volume} {t("cubicMetersLabel")}
            </span>
          </div>
        </div>
      </div>

      {/* Table (DataTable scrolls internally; header stays sticky) */}
      <div className="mt-2 min-h-0 flex-1 px-4 pb-4">
        <DataTable
          columns={columns}
          data={jobs}
          autoScrollToSelected={false}
          loading={isLoading}
          rowKey={(row) => row.id.toString()}
          selectedRowKeys={[...selected]}
          onRowClick={toggleJob}
          stickyHeader
          namespace={Namespace.Routes}
          empty={t("noJobsAvailable")}
          className="h-full"
        />
      </div>
    </div>
  );
};

interface FilterChipProps {
  label: string;
  onRemove: () => void;
}

const FilterChip: React.FC<FilterChipProps> = ({ label, onRemove }) => (
  <span className="flex h-6 items-center rounded-2xl bg-blue-600 p-1 text-xs font-medium text-white">
    <span className="px-2">{label}</span>
    <button type="button" onClick={onRemove} className="flex items-center" aria-label="remove">
      <IcCancel className="h-4 w-4" />
    </button>
  </span>
);

interface FilterMultiSelectProps {
  inputId: string;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  values: string[];
  options: { value: string; label: string }[];
  /** Trigger text once more than one option is picked (chips carry the names). */
  summary: (count: number) => string;
  onToggle: (value: string) => void;
}

const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  inputId,
  label,
  placeholder,
  searchPlaceholder,
  emptyText,
  values,
  options,
  summary,
  onToggle,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedLabel =
    values.length === 0
      ? undefined
      : values.length === 1
        ? options.find((o) => o.value === values[0])?.label
        : summary(values.length);
  const filtered = search.trim() ? options.filter((o) => matchesSearch(o.label, search)) : options;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-primary">{label}</span>
      <SearchablePopover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearch("");
        }}
        trigger={
          <button className="flex h-8 w-full items-center justify-between rounded-lg border border-gray-200 px-2 focus:outline-none">
            <span
              className={`truncate text-sm ${selectedLabel ? "text-text-primary" : "text-text-secondary"}`}
            >
              {selectedLabel || placeholder}
            </span>
          </button>
        }
        searchValue={search}
        onSearchChange={setSearch}
        inputId={inputId}
        placeholder={searchPlaceholder}
        contentClassName="w-[228px]"
      >
        {filtered.map((option) => (
          // A label, not a button: the Radix checkbox is itself a <button> and
          // cannot be nested in one. The popover stays open — picking several
          // groups in a row is the point. A <label> can't drive a Radix
          // Checkbox (it renders a <button>, which labels don't activate), so
          // the whole row is the click/keyboard target and the checkbox is
          // presentational — pointer-events-none routes its own clicks to the row.
          <div
            key={option.value}
            role="button"
            tabIndex={0}
            aria-pressed={values.includes(option.value)}
            onClick={() => onToggle(option.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle(option.value);
              }
            }}
            className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
          >
            <Checkbox
              checked={values.includes(option.value)}
              tabIndex={-1}
              className="pointer-events-none"
            />
            <span className="truncate">{option.label}</span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-gray-500">{emptyText}</div>
        )}
      </SearchablePopover>
    </div>
  );
};
